// What the admin extensions page says about an extension: its name and
// description in the UI language, what it requires, what depends on it, and
// why a change was refused (ADR-0051). Pure — the page and its dialog own the
// queries and the mutation.
import type { TFunction } from 'i18next'
import { ApiError } from '@/services/api/client'
import type { Extension } from '@/services/api/extensions'

type Named = Pick<Extension, 'id' | 'name'>

/** The name in the UI language; the registry's own name for an extension no locale knows. */
export function extensionName(t: TFunction, ext: Named): string {
  return t(`extensions.catalog.${ext.id}.name`, { defaultValue: ext.name })
}

/** The description in the UI language, falling back to the registry's own. */
export function extensionDescription(t: TFunction, ext: Pick<Extension, 'id' | 'description'>) {
  return t(`extensions.catalog.${ext.id}.description`, { defaultValue: ext.description })
}

/** The names of *ids*, joined for a sentence; an id the list does not know shows as it is. */
export function extensionNames(
  t: TFunction,
  ids: readonly string[],
  extensions: readonly Extension[]
): string {
  return ids
    .map(id => extensionName(t, extensions.find(ext => ext.id === id) ?? { id, name: id }))
    .join(', ')
}

/** The extensions switched on now that need *ext*: switching it off switches them off too. */
export function enabledDependents(ext: Pick<Extension, 'id'>, extensions: readonly Extension[]) {
  return extensions.filter(other => other.enabled && (other.requires ?? []).includes(ext.id))
}

/**
 * What to do about the required extensions that are off: switch them on, or
 * nothing here when the deployment itself switches one off.
 */
export function requirementHint(
  t: TFunction,
  missing: readonly string[],
  extensions: readonly Extension[]
): string {
  const names = extensionNames(t, missing, extensions)
  const deploymentOff = missing.some(
    id => extensions.find(ext => ext.id === id)?.available === false
  )
  return t(deploymentOff ? 'extensions.requiresUnavailableHint' : 'extensions.requiresHint', {
    names,
  })
}

/**
 * Why the admin toggle cannot move, or null when it can: the deployment switches
 * the extension off, or an extension it requires is off (the API refuses both).
 */
export function switchLock(
  t: TFunction,
  ext: Extension,
  extensions: readonly Extension[]
): string | null {
  if (!ext.available) return t('extensions.unavailableHint')
  const missing = ext.missing_requirements ?? []
  return missing.length === 0 ? null : requirementHint(t, missing, extensions)
}

type RefusalDetail = { code?: unknown; requires?: unknown }

function refusalDetail(error: unknown): RefusalDetail | null {
  if (!(error instanceof ApiError)) return null
  const detail = (error.data as { detail?: unknown } | null | undefined)?.detail
  return detail && typeof detail === 'object' ? (detail as RefusalDetail) : null
}

/** The refusal's code; the env-flag refusal is a 409 with a plain-text detail. */
function refusalCode(error: unknown, detail: RefusalDetail | null): string {
  if (typeof detail?.code === 'string') return detail.code
  if (error instanceof ApiError && error.status === 409) return 'extension_unavailable'
  return 'updateFailed'
}

/** The toast for a refused change, keyed on the API's `detail.code`. */
export function updateErrorMessage(
  t: TFunction,
  error: unknown,
  extensions: readonly Extension[]
): string {
  const detail = refusalDetail(error)
  const requires = Array.isArray(detail?.requires)
    ? detail.requires.filter((id): id is string => typeof id === 'string')
    : []
  return t(`extensions.errors.${refusalCode(error, detail)}`, {
    names: extensionNames(t, requires, extensions),
    defaultValue: t('extensions.errors.updateFailed'),
  })
}
